'use client';

import { useEffect } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { reportError } from '@/hooks/use-error-reporter';

/**
 * Global error page — catches errors at the root layout level (critical).
 *
 * Minimal HTML (no layout, no providers) to ensure this page always renders
 * even if the entire app context is broken. Uses hardcoded styles instead of
 * Tailwind to avoid depending on CSS providers.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // Report error to error-logs (best-effort)
  useEffect(() => {
    try {
      reportError(error, {
        component: 'global-error.tsx',
        severity: 'critical',
        metadata: { digest: error.digest, scope: 'root' },
      });
    } catch {
      // Silently fail if error reporting is unavailable
      console.error('[GlobalError] Failed to report critical error', error);
    }
  }, [error]);

  return (
    <html lang="en">
      <head>
        <title>Application Error</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <style>{`
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
            background-color: #f5f5f5;
            color: #333;
          }
          .error-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            gap: 24px;
            padding: 32px 16px;
            text-align: center;
          }
          .error-icon {
            width: 64px;
            height: 64px;
            color: #dc2626;
          }
          h1 {
            font-size: 28px;
            font-weight: 600;
            margin-bottom: 8px;
          }
          p {
            font-size: 16px;
            color: #666;
            max-width: 448px;
            margin-bottom: 8px;
          }
          .error-detail {
            font-size: 12px;
            color: #999;
            background-color: #f9f9f9;
            padding: 12px 16px;
            border-radius: 4px;
            max-width: 512px;
            word-break: break-word;
          }
          button {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 10px 24px;
            background-color: #4f46e5;
            color: white;
            border: none;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: background-color 0.2s;
            margin-top: 16px;
          }
          button:hover {
            background-color: #4338ca;
          }
          button:active {
            background-color: #3730a3;
          }
          .icon {
            width: 16px;
            height: 16px;
          }
          .error-id {
            font-size: 11px;
            color: #aaa;
            margin-top: 16px;
          }
        `}</style>
      </head>
      <body>
        <div className="error-container">
          <svg
            className="error-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>

          <div>
            <h1>Application Error</h1>
            <p>
              The application encountered a critical error and cannot continue.
            </p>
          </div>

          {error.message && (
            <div className="error-detail">
              {error.message}
            </div>
          )}

          <button onClick={reset}>
            <svg
              className="icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Try again
          </button>

          {error.digest && (
            <div className="error-id">
              Error ID: {error.digest}
            </div>
          )}
        </div>
      </body>
    </html>
  );
}
